import type { ReactNode } from 'react';
import { useState } from 'react';

import classNames from 'classnames';
import omit from 'lodash/omit';

import style from './Layout.module.css';

import type { DefaultAppNavProps } from '../AppNav';
import { AppNav } from '../AppNav';
import type { NavigationItem } from '../NavItem';
import { VerticalNavigation } from '../VerticalNavigation';

export type LayoutTheme = 'blank' | 'chickpea' | 'kale' | 'oyster';

export interface LayoutProps {
  /** Content to render as footer. */
  footer?: ReactNode;
  /** A banner to display before the app navigation. */
  banner?: ReactNode;
  /**
   * The navigation element to display in the header.
   *
   * @deprecated Use {@link LayoutProps.appNav} instead to render a {@link AppNav}
   *   component.
   */
  navigation?: ReactNode;
  /**
   * Props for the app navigation. If undefined, no navigation is rendered.
   *
   * Note: {@link AppNavProps.subNavItems} must be set in the Layout
   * {@link LayoutProps.subNav} prop, not to this prop.
   */
  appNav?: Omit<DefaultAppNavProps, 'subNavItems'>;
  /**
   * Items for the sub navigation, displayed on the left (on large viewports) or
   * in the app navigation (on small viewports).
   */
  subNav?: DefaultAppNavProps['subNavItems'];
  /** The content of the `<main>` HTML element. */
  children?: ReactNode;
  /**
   * Toggle the main theme for the app.
   *
   * - `blank`: white background and no spacings.
   * - `chickpea`: a spaced theme with a chickpea background and spacings.
   * - `kale`: a spaced theme with a light blue background.
   *
   * @defaultValue blank
   */
  theme?: LayoutTheme;
}

/**
 * Create the full-page layouts used by The Farmers’ Dog, including banners,
 * header, footer and sidebar. The Layout component should always wrap a
 * {@link Page} component, ideally within a Route.
 *
 * @example
 *
 * ```
 * <Layout>
 *   <Route path="/account">
 *     <Page>My Account Page</Page>
 *   </Route>
 * </Layout>;
 * ```
 *
 * @see https://corgi-x.tfd.engineering/components/layout
 */
export function Layout(props: LayoutProps) {
  const { subNav, appNav } = props;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const className = classNames(
    {
      [style.withSubNav]: subNav && subNav.length > 0,
      [style.withAppNav]: Boolean(appNav),
      [style.withMenu]: isMenuOpen,
    },
    style.layout
  );
  // When appNav has an overlay mode set, the main content should shift up to appear behind the app nav.
  const contentClassName = classNames(style.content, {
    [style.shiftUp]: appNav?.overlayMode && appNav?.overlayMode !== 'none',
  });
  const showHeader = !props.navigation && (appNav || subNav);
  const handleMenuOpen = () => {
    setIsMenuOpen(true);
    appNav?.onMenuOpen?.();
  };
  const handleMenuClose = () => {
    setIsMenuOpen(false);
    appNav?.onMenuClose?.();
  };

  return (
    <div className={className} data-tfd-theme={props.theme}>
      {props.banner && !isMenuOpen && <div>{props.banner}</div>}
      {props.navigation}
      {showHeader && (
        <header className={style.header}>
          <AppNav
            shiftLogoUp={Boolean(props.banner)}
            subNavItems={subNav}
            {...appNav}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
          />
        </header>
      )}
      <div className={contentClassName}>
        {subNav && subNav.length > 0 && (
          <aside className={style.aside}>
            <VerticalNavigation
              aria-label="Sub Navigation"
              items={subNav.map(
                item => omit(item, 'alwaysVisible') as NavigationItem
              )}
            />
          </aside>
        )}
        <div className={style.main}>{props.children}</div>
      </div>
      {props.footer}
    </div>
  );
}
